import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { useThroughput } from '@/hooks/useDashboard'

interface Props {
  days: number
}

const STATUS_COLORS = {
  completed: '#22c55e',
  reviewed: '#0000ff',
  review_required: '#f59e0b',
  failed: '#ef4444',
}

export function ThroughputChart({ days }: Props) {
  const { data, isLoading, isError } = useThroughput(days)

  const chartData = (data ?? []).map((p) => ({
    ...p,
    label: format(parseISO(p.date), 'd MMM'),
  }))

  const total = chartData.reduce(
    (acc, p) => acc + p.completed + p.reviewed + p.review_required + p.failed,
    0
  )

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Throughput</CardTitle>
        <p className="text-xs text-neutral-500">
          Invoices processed per day, by terminal status — last {days} days
          {total > 0 ? ` · ${total} total` : ''}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && <div className="h-64 animate-pulse rounded bg-neutral-100" />}
        {isError && (
          <div className="flex h-64 items-center justify-center text-sm text-red-600">
            Could not load throughput.
          </div>
        )}
        {!isLoading && !isError && total === 0 && (
          <div className="flex h-64 items-center justify-center text-sm text-neutral-500">
            No invoices processed in this window.
          </div>
        )}
        {!isLoading && !isError && total > 0 && (
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#f8fafc' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="completed"
                  stackId="status"
                  name="Completed"
                  fill={STATUS_COLORS.completed}
                />
                <Bar
                  dataKey="reviewed"
                  stackId="status"
                  name="Reviewed/Exported"
                  fill={STATUS_COLORS.reviewed}
                />
                <Bar
                  dataKey="review_required"
                  stackId="status"
                  name="Pending review"
                  fill={STATUS_COLORS.review_required}
                />
                <Bar dataKey="failed" stackId="status" name="Failed" fill={STATUS_COLORS.failed} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
